import axios, { sendRequest } from "@api/clients/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Delivery } from "@types_def/models/delivery.types";
import { DeliveryStatus, DeliveryType, TwoWayDelivery } from "@types_def/models/delivery.types";
import type { Vehicle } from "@types_def/models/vehicle.types";
import { VehicleType } from "@types_def/models/vehicle.types";
import { RoutePaths } from "@utils/constants/routes";
import type { SchemaKeys } from "@utils/functions/Delivery/schema";
import type { AxiosResponse } from "axios";
import { t } from "i18next";
import { toast } from "react-toastify";

import {
  cancelDeliveryCacheUpdates,
  invalidateDeliveryCache,
  patchDeliveryCache,
  removeDeliveryDetailCache,
  removeDeliveryFromListsCache,
  restoreDeliveryCache,
  snapshotDeliveryCache,
} from "@/features/delivery/cache";
import {
  useCancelDeliveryMutation,
  useConfirmDeliveryMutation,
  useDeleteDeliveryMutation,
  useFindDeliveryLogsQuery,
  useUpdateDeliveryMutation,
  useUpdateDeliveryStatusMutation,
  useUpdateDeliveryTypeMutation,
  useUpdateVehicleMutation,
} from "@/libs/generated/hooks";
import { graphqlClient } from "@/libs/graphql-fetcher";

const URL = String(import.meta.env.VITE_APP_API_BASE_URL ?? "");

type DeliveryMutationContext = {
  snapshot?: ReturnType<typeof snapshotDeliveryCache>;
  snapshots?: Array<ReturnType<typeof snapshotDeliveryCache>>;
};
type ConfirmDeliveryMutationInput = {
  ConfirmDeliveryInput: {
    deliveryId: number;
  };
};
type CancelDeliveryMutationResult = {
  cancelDelivery: {
    id: number;
  };
};
type CancelDeliveryMutationInput = {
  CancelDeliveryInput: {
    id: number;
    reason: unknown;
  };
};
type DeleteDeliveryMutationInput = {
  DeleteInput: {
    id: number;
  };
};
type UpdateDeliveryStatusMutationInput = {
  UpdateDeliveryStatusInput: {
    id: number;
    status: DeliveryStatus;
  };
};
type UpdateDeliveryTypeMutationInput = {
  UpdateDeliveryTypeInput: {
    id: number;
    type: DeliveryType;
  };
};
type ConfirmDeliveriesMutationVariables = {
  ConfirmDeliveriesInput: {
    deliveryIds: number[];
  };
};
type UpdateDeliveriesStatusMutationVariables = {
  UpdateDeliveriesStatusInput: {
    ids: number[];
    status: DeliveryStatus;
  };
};

const CONFIRM_DELIVERIES_MUTATION = /* GraphQL */ `
  mutation ConfirmDeliveries($ConfirmDeliveriesInput: ConfirmDeliveriesInput!) {
    confirmDeliveries(ConfirmDeliveriesInput: $ConfirmDeliveriesInput) {
      id
      status
    }
  }
`;

const UPDATE_DELIVERIES_STATUS_MUTATION = /* GraphQL */ `
  mutation UpdateDeliveriesStatus($UpdateDeliveriesStatusInput: updateDeliveriesStatusInput!) {
    updateDeliveriesStatus(UpdateDeliveriesStatusInput: $UpdateDeliveriesStatusInput) {
      id
      status
    }
  }
`;

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message.split(": ")[1] ?? error.message;
  }

  return t("network.errors.unknown");
}

const initialAddress = {
  latitude: "",
  longitude: "",
  city: "",
  address: "",
  postalCode: "",
  place_id: "",
  addressPrecision: "",
};

const commun = {
  comment: "",
  clientFirstName: "",
  clientLastName: "",
  clientEmail: "",
  clientDrivingLicenceNumber: "",
  carAvailabilityDate: null,
  carPickUpDate: null,
  carDeliveryDeadline: null,
  clientPhone: "",
  clientSiret: "",
  clientCompany: "",
  representativeFirstName: "",
  representativeLastName: "",
  fromAddress: initialAddress,
  toAddress: initialAddress,
  representativeEmail: "",
  representativePhone: "",
  representativeSiret: "",
  representativeCompany: "",
  category: "STANDARD",
  status: "PROFESSIONAL",
  docs: [
    // {
    //   name: "ALB_PV.pdf",
    //   key: "https://alb-media.s3.eu-west-3.amazonaws.com/defaults/PV+ALB.pdf",
    //   url: "https://alb-media.s3.eu-west-3.amazonaws.com/defaults/PV+ALB.pdf",
    //   type: "application/pdf",
    // },
  ],
};
const vehicle = {
  vin: "",
  carType: "A",
  vehicleCode: "",
  marque: "",
  model: "",
  toRetrieve: false,
  type: VehicleType.GAS,
};

const StandardDeliveryData = {
  ...commun,
  numberOfVehicles: 1,
  type: DeliveryType.STANDARD,
  twoWayDeliverType: null,
  vehicles: [vehicle],
};

const ToReturnDeliveryData = {
  ...StandardDeliveryData,
  type: DeliveryType.TO_RETURN,
};
const TwoWayDeliveryData = {
  ...commun,
  numberOfVehicles: 2,
  type: DeliveryType.TWO_WAY_DELIVERY,
  twoWayDeliverType: TwoWayDelivery.EXCHANGE,
  toReturnFromAddress: initialAddress,
  toReturnToAddress: undefined,
  vehicles: [
    vehicle,
    {
      ...vehicle,
      toRetrieve: true,
    },
  ],
};
const initialDelivery = {
  standard: {
    ...commun,
    numberOfVehicles: 1,
    type: DeliveryType.STANDARD,
    twoWayDeliverType: null,
    vehicles: [vehicle],
  },
  "to-return": {
    ...commun,
    numberOfVehicles: 1,
    type: DeliveryType.TO_RETURN,
    twoWayDeliverType: null,
    vehicles: [vehicle],
  },
  "two-way-delivery": {
    ...commun,
    numberOfVehicles: 2,
    twoWayDeliverType: TwoWayDelivery.EXCHANGE,
    type: DeliveryType.TWO_WAY_DELIVERY,
    toReturnFromAddress: initialAddress,
    toReturnToAddress: undefined,
    vehicles: [
      vehicle,
      {
        ...vehicle,
        toRetrieve: Boolean(true),
      },
    ],
  },
} as unknown as Delivery;

export type defaultValues = {
  car?: Vehicle;
  documents?: Array<{ name: string; key: string; url: string; deliveryType: DeliveryType }>;
};

export function generateInitialDelivery(type: SchemaKeys) {
  switch (type) {
    case "standard":
      return StandardDeliveryData;
    case "okm":
      return {
        ...{
          ...StandardDeliveryData,
          type: DeliveryType.OKM,
        },
      };
    case "to-return":
      return {
        ...ToReturnDeliveryData,
      };
    case "two-way-delivery":
      return {
        ...TwoWayDeliveryData,
      };
    default:
      return {};
  }
}
async function deleteDeliveryDoc(key: string) {
  const fileKeyToDelete = encodeURIComponent(key);
  await axios.delete(`/s3/${fileKeyToDelete}`);
}
function useFindDeliveryLog({
  deliveryId,
  page,
  pageSize = 5,
}: {
  deliveryId: number;
  page: number;
  pageSize?: number;
}) {
  return useFindDeliveryLogsQuery(
    { deliveryId, page, pageSize },
    { select: (data) => data.findDeliveryLogs }
  );
}

function useUpdateDelivery() {
  const client = useQueryClient();
  return useUpdateDeliveryMutation({
    onSuccess: async (data) => {
      await invalidateDeliveryCache(client, data.updateDelivery.id);
      toast(t("network.delivery.updated"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (error: unknown) => {
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

function useUpdateVehicle() {
  const queryClient = useQueryClient();

  return useUpdateVehicleMutation({
    onSuccess: async () => {
      toast(t("network.vehicle.updated"), {
        position: "bottom-right",
        type: "success",
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["findDeliveryById"] }),
        queryClient.invalidateQueries({ queryKey: ["findAllDelivery"] }),
      ]);
    },
    onError: (error: unknown) => {
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

function useConfirmDelivery() {
  const queryClient = useQueryClient();

  return useConfirmDeliveryMutation<unknown, DeliveryMutationContext>({
    onMutate: async (variables: ConfirmDeliveryMutationInput) => {
      const deliveryId = variables.ConfirmDeliveryInput.deliveryId;
      await cancelDeliveryCacheUpdates(queryClient, deliveryId);
      const snapshot = snapshotDeliveryCache(queryClient, deliveryId);

      patchDeliveryCache(queryClient, deliveryId, { status: DeliveryStatus.CONFIRMED });

      return { snapshot };
    },
    onSuccess: async (_data, variables: ConfirmDeliveryMutationInput) => {
      await invalidateDeliveryCache(queryClient, variables.ConfirmDeliveryInput.deliveryId);
      toast(t("network.delivery.confirmed"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (error: unknown, _variables, context) => {
      if (context) {
        restoreDeliveryCache(queryClient, context.snapshot);
      }
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

function useConfirmDeliveries() {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, ConfirmDeliveriesMutationVariables, DeliveryMutationContext>(
    {
      mutationKey: ["confirmDeliveries"],
      mutationFn: (variables) => graphqlClient.request(CONFIRM_DELIVERIES_MUTATION, variables),
      onMutate: async (variables: ConfirmDeliveriesMutationVariables) => {
        const deliveryIds = variables.ConfirmDeliveriesInput.deliveryIds;
        await Promise.all(deliveryIds.map((id) => cancelDeliveryCacheUpdates(queryClient, id)));
        const snapshots = deliveryIds.map((id) => snapshotDeliveryCache(queryClient, id));

        deliveryIds.forEach((id) => {
          patchDeliveryCache(queryClient, id, { status: DeliveryStatus.CONFIRMED });
        });

        return { snapshots };
      },
      onSuccess: async (_data, variables: ConfirmDeliveriesMutationVariables) => {
        const deliveryIds = variables.ConfirmDeliveriesInput.deliveryIds;
        await Promise.all(deliveryIds.map((id) => invalidateDeliveryCache(queryClient, id)));
        toast(t("network.delivery.confirmed"), {
          position: "bottom-right",
          type: "success",
        });
      },
      onError: (error: unknown, _variables, context) => {
        if (context) {
          context.snapshots.forEach((snapshot: ReturnType<typeof snapshotDeliveryCache>) => {
            restoreDeliveryCache(queryClient, snapshot);
          });
        }
        toast(getErrorMessage(error), {
          position: "bottom-right",
          type: "error",
        });
      },
    }
  );
}

function useCancelDelivery() {
  const queryClient = useQueryClient();

  return useCancelDeliveryMutation<unknown, DeliveryMutationContext>({
    onMutate: async (variables: CancelDeliveryMutationInput) => {
      const deliveryId = variables.CancelDeliveryInput.id;
      await cancelDeliveryCacheUpdates(queryClient, deliveryId);
      const snapshot = snapshotDeliveryCache(queryClient, deliveryId);

      patchDeliveryCache(queryClient, deliveryId, {
        status: DeliveryStatus.CANCELLED_BY_ADMIN,
        cancelledAt: new Date(),
        cancelReason: variables.CancelDeliveryInput.reason,
      });

      return { snapshot };
    },
    onSuccess: async (data: CancelDeliveryMutationResult) => {
      await invalidateDeliveryCache(queryClient, data.cancelDelivery.id);
      toast(t("network.delivery.cancelled"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (error: unknown, _variables, context) => {
      if (context) {
        restoreDeliveryCache(queryClient, context.snapshot);
      }
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

function useDeleteDelivery() {
  const queryClient = useQueryClient();

  return useDeleteDeliveryMutation<unknown, DeliveryMutationContext>({
    onMutate: async (variables: DeleteDeliveryMutationInput) => {
      const deliveryId = variables.DeleteInput.id;
      await cancelDeliveryCacheUpdates(queryClient, deliveryId);
      const snapshot = snapshotDeliveryCache(queryClient, deliveryId);

      removeDeliveryFromListsCache(queryClient, deliveryId);

      return { snapshot };
    },
    onSuccess: async (_data, variables: DeleteDeliveryMutationInput) => {
      const deliveryId = variables.DeleteInput.id;
      removeDeliveryDetailCache(queryClient, deliveryId);
      await queryClient.invalidateQueries({ queryKey: ["findAllDelivery"] });
      toast(t("network.delivery.deleted"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (error: unknown, _variables, context) => {
      if (context) {
        restoreDeliveryCache(queryClient, context.snapshot);
      }
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

function useUpdateDeliveryStatus() {
  const queryClient = useQueryClient();

  return useUpdateDeliveryStatusMutation<unknown, DeliveryMutationContext>({
    onMutate: async (variables: UpdateDeliveryStatusMutationInput) => {
      const { id, status } = variables.UpdateDeliveryStatusInput;
      await cancelDeliveryCacheUpdates(queryClient, id);
      const snapshot = snapshotDeliveryCache(queryClient, id);

      patchDeliveryCache(queryClient, id, { status });

      return { snapshot };
    },
    onSuccess: async (_data, variables: UpdateDeliveryStatusMutationInput) => {
      await invalidateDeliveryCache(queryClient, variables.UpdateDeliveryStatusInput.id);
      toast(t("network.delivery.status.updated"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (error: unknown, _variables, context) => {
      if (context) {
        restoreDeliveryCache(queryClient, context.snapshot);
      }
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

function useUpdateDeliveriesStatus() {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    unknown,
    UpdateDeliveriesStatusMutationVariables,
    DeliveryMutationContext
  >({
    mutationKey: ["updateDeliveriesStatus"],
    mutationFn: (variables) => graphqlClient.request(UPDATE_DELIVERIES_STATUS_MUTATION, variables),
    onMutate: async (variables: UpdateDeliveriesStatusMutationVariables) => {
      const { ids, status } = variables.UpdateDeliveriesStatusInput;
      await Promise.all(ids.map((id) => cancelDeliveryCacheUpdates(queryClient, id)));
      const snapshots = ids.map((id) => snapshotDeliveryCache(queryClient, id));

      ids.forEach((id) => {
        patchDeliveryCache(queryClient, id, { status });
      });

      return { snapshots };
    },
    onSuccess: async (_data, variables: UpdateDeliveriesStatusMutationVariables) => {
      const ids = variables.UpdateDeliveriesStatusInput.ids;
      await Promise.all(ids.map((id) => invalidateDeliveryCache(queryClient, id)));
      toast(t("network.delivery.status.updated"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (error: unknown, _variables, context) => {
      if (context) {
        context.snapshots.forEach((snapshot: ReturnType<typeof snapshotDeliveryCache>) => {
          restoreDeliveryCache(queryClient, snapshot);
        });
      }
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

function useUpdateDeliveryType() {
  const queryClient = useQueryClient();

  return useUpdateDeliveryTypeMutation<unknown, DeliveryMutationContext>({
    onMutate: async (variables: UpdateDeliveryTypeMutationInput) => {
      const { id, type } = variables.UpdateDeliveryTypeInput;
      await cancelDeliveryCacheUpdates(queryClient, id);
      const snapshot = snapshotDeliveryCache(queryClient, id);

      patchDeliveryCache(queryClient, id, { type });

      return { snapshot };
    },
    onSuccess: async (_data, variables: UpdateDeliveryTypeMutationInput) => {
      await invalidateDeliveryCache(queryClient, variables.UpdateDeliveryTypeInput.id);
      toast(t("network.delivery.type.updated"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (error: unknown, _variables, context) => {
      if (context) {
        restoreDeliveryCache(queryClient, context.snapshot);
      }
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

function useGetSmsLink(id: number) {
  return useQuery({
    queryKey: ["smsLink", id],
    queryFn: async () => {
      const res = await sendRequest<never, string>(
        "get",
        `${URL}delivery/sms-confirmation-url/${id}`
      );
      const apiUrl = URL;
      const route = RoutePaths.deliveryCustomerConfirmation;
      const token = res.data;
      return `${apiUrl}${route}?token=${token}`;
    },
  });
}

function useSendSMS() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: number; clientPhone: string; message: string }) =>
      axios.request<AxiosResponse<unknown>>({
        method: "POST",
        url: `${URL}sms/send-delivery-confirmation`,
        data: {
          deliveryId: data.id,
          recipient: data.clientPhone,
          message: data.message,
        },
      }),
    onSuccess: async (_data, variables) => {
      toast(t("network.delivery.sms.sent"), {
        position: "bottom-right",
        type: "success",
      });
      await invalidateDeliveryCache(client, variables.id);
    },
    onError: (error: unknown) => {
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

function useSendRatingEmail() {
  return useMutation({
    mutationFn: async (id: number) =>
      axios.request({
        method: "POST",
        url: `${URL}email/send-rate-email`,
        data: { deliveryId: id },
      }),
    onSuccess: () => {
      toast(t("network.delivery.rating.email.sent"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (error: unknown) => {
      toast(getErrorMessage(error), {
        position: "bottom-right",
        type: "error",
      });
    },
  });
}

export {
  deleteDeliveryDoc,
  initialDelivery,
  useCancelDelivery,
  useConfirmDeliveries,
  useConfirmDelivery,
  useDeleteDelivery,
  useFindDeliveryLog,
  useGetSmsLink,
  useSendRatingEmail,
  useSendSMS,
  useUpdateDeliveriesStatus,
  useUpdateDelivery,
  useUpdateDeliveryStatus,
  useUpdateDeliveryType,
  useUpdateVehicle,
};
